import {
  Grid,
  GridCellProps,
  GridDetailRowProps,
  GridHeaderCellProps,
  GridItemChangeEvent,
} from '@progress/kendo-react-grid';
import { isNull } from 'lodash';
import { createRef, useCallback, useEffect } from 'react';
import {
  useBudgetPeriodColumn,
  useBudgetPeriodSubColumn,
} from 'src/assets/static/view_budget_period/viewBudgetPeriodColumn';
import GridWrapper from 'src/components/GridWrapper';
import Layout from 'src/components/Layout';
import ExpandCollapseCell from 'src/components/custom-cells/ExpandCollapseCell';
import useViewBudgetPeriod from 'src/hooks/useViewBudgetPeriod';
import useWindowSize from 'src/hooks/useWindowSize';
import { GLCodesBudgets } from 'src/services/useGlCodesBudgetApi';
import { round } from 'src/utils/number';
import ViewBudgetPeriodToolBar from './ViewBudgetPeriodToolBar';

const DATA_ITEM_KEY: string = 'rowId';
const defaultProps = {
  DataItemKey: DATA_ITEM_KEY,
  resizable: true,
  showColumnMenu: false,
} as const;

const ViewBudgetPeriodTable = () => {
  const {
    expand,
    toggleExpand,
    setBudgetPeriods,
    budgetPeriodDataWithErrors,
    poDetails,
    glCodeSummary,
    isBudgetPeriodLoading,
    isPoDetailsLoading,
    isSummaryLoading,
    isReadOnly,
    getGlCodesBudget,
    initialCheckbookState,
    t,
    isDropDownLoading,
  } = useViewBudgetPeriod();
  const { height } = useWindowSize();
  const columns = useBudgetPeriodColumn(isReadOnly);
  const ViewBudgetSubColumns = useBudgetPeriodSubColumn();

  const handleItemChange = useCallback(
    async (e: GridItemChangeEvent) => {
      const { field = '', value } = e;
      const { rowId, percentageAmount } = e.dataItem;

      const dollarAmount = round((Number(poDetails.poTotal) * Number(value || 0)) / 100, 2);
      const dollarPercentage = round((Number(value || 0) / Number(poDetails.poTotal)) * 100, 2);

      const isGlSummaryPresent =
        glCodeSummary.filter((summary) => summary.glPeriodId === value).length > 0;

      let newGlSummary: any[] = [];
      if (field === 'glPeriodId' && !isNull(value)) {
        if (!isGlSummaryPresent) {
          const { items = [] }: GLCodesBudgets = await getGlCodesBudget({
            glPeriodId: value,
            checkbookDataState: initialCheckbookState,
            clientSide: true,
          });

          let uniqueGlCodes: any = [];

          const filteredData = glCodeSummary.filter((entry) => {
            if (!uniqueGlCodes.includes(entry.glCode)) {
              uniqueGlCodes.push(entry.glCode);
              return true;
            }
            return false;
          });

          newGlSummary = filteredData.map((code) => {
            const distributionAmount = round((code.totalAmount * percentageAmount) / 100, 2);
            const beginBalanceAmount =
              items.find((item) => item.code === code.glCode)?.remainingBudgetAmount ?? 0;
            return {
              glPeriodId: value,
              glCode: code.glCode,
              glCodeContent: code.glCodeContent,
              beginBalanceAmount,
              totalAmount: code.totalAmount,
              endingBalanceAmount: beginBalanceAmount - distributionAmount,
              distributionPercentage: percentageAmount,
              distributionAmount: percentageAmount > 0 ? distributionAmount : 0,
            };
          });
        }
      }
      setBudgetPeriods((prev) => [
        ...prev.map((item) => ({
          ...item,
          ...(rowId === item.rowId && {
            [field]: value,
            ...(field === 'percentageAmount'
              ? {
                  amount: dollarAmount,
                  items: item.items.map((data) => {
                    const distributionAmount = round((data.totalAmount * Number(value)) / 100, 2);
                    return {
                      ...data,
                      endingBalanceAmount: data.beginBalanceAmount - distributionAmount,
                      distributionPercentage: value,
                      distributionAmount,
                    };
                  }),
                }
              : {}),
            ...(field === 'amount'
              ? {
                  amount: value === '.' || value === '' || !value ? value : Number(value),
                  percentageAmount: dollarPercentage,
                  items: item.items.map((data) => {
                    const distributionAmount = round(
                      (data.totalAmount * dollarPercentage) / 100,
                      2
                    );

                    return {
                      ...data,
                      endingBalanceAmount: data.beginBalanceAmount - distributionAmount,
                      distributionPercentage: dollarPercentage,
                      distributionAmount,
                    };
                  }),
                }
              : {}),
            ...(field === 'glPeriodId'
              ? {
                  items: isGlSummaryPresent
                    ? glCodeSummary
                        .filter((summary) => summary.glPeriodId === value)
                        .map((data) => ({
                          ...data,
                          glCodeContent: data.glCodeContent,
                          distributionPercentage: item.percentageAmount,
                          distributionAmount: round(
                            data.totalAmount * (item.percentageAmount / 100),
                            2
                          ),
                        }))
                    : newGlSummary,
                }
              : {}),
          }),
        })),
      ]);
    },
    [getGlCodesBudget, glCodeSummary, initialCheckbookState, poDetails?.poTotal, setBudgetPeriods]
  );

  const DetailComponent = useCallback(
    (props: GridDetailRowProps) => (
      <GridWrapper
        dataSource={props.dataItem.items}
        columns={ViewBudgetSubColumns}
        DataItemKey="field"
        style={{ maxHeight: '120px' }}
      />
    ),
    [ViewBudgetSubColumns]
  );
  const headerCellRender = (td: any, props: GridHeaderCellProps) => {
    const onExpand = () => {
      setBudgetPeriods((prev) => [...prev].map((item) => ({ ...item, expanded: !expand })));
      toggleExpand();
    };
    if (td && props.field === 'expanded') {
      return (
        <>
          <ExpandCollapseCell
            expand={expand}
            onExpand={onExpand}
            title={t('Expand/Collapse All')}
          />
          <div className="pb-[6px]" />
        </>
      );
    }
    return td;
  };

  const cellRender = (
    tdElement: React.ReactElement<HTMLTableCellElement>,
    cellProps: GridCellProps
  ) => {
    const { field = '', dataItem, columnIndex } = cellProps;

    const handleExpand = () => {
      setBudgetPeriods((prev) =>
        [...prev].map((item) => {
          if (item?.rowId === dataItem.rowId) {
            item.expanded = !dataItem.expanded;
          }
          return item;
        })
      );
      toggleExpand();
    };

    if (field === 'expanded') {
      return (
        <td aria-colindex={columnIndex} role={'gridcell'} className="pb-[6px]">
          <ExpandCollapseCell
            expand={dataItem.expanded}
            onExpand={handleExpand}
            title={t('Expand/Collapse')}
            plusIconColor="#0E80B4"
            minusIconColor="#0E80B4"
          />
        </td>
      );
    }
    return tdElement;
  };

  const gridRef = createRef<Grid>();
  useEffect(() => {
    if (gridRef.current?.element) {
      const grid = gridRef.current?.element;
      const header = grid?.getElementsByClassName('k-grid-header')[0] as HTMLElement;
      const footer = grid?.getElementsByClassName('k-grid-footer')[0] as HTMLElement;
      const tableHeight = grid?.getElementsByClassName('k-grid-table')[0] as HTMLElement;
      const content = grid?.getElementsByClassName('k-grid-content')[0] as HTMLElement;
      const scrollbarWidth = content.offsetWidth - content.clientWidth;
      const gridHeight = height - 274;
      if (header && footer) {
        if (tableHeight.offsetHeight > gridHeight) {
          header.style.paddingInlineEnd = `${scrollbarWidth}px`;
          footer.style.paddingInlineEnd = `${scrollbarWidth}px`;
        } else {
          header.style.paddingInlineEnd = '0px';
          footer.style.paddingInlineEnd = '0px';
        }
      }
    }
  }, [budgetPeriodDataWithErrors.length, gridRef, height]);

  return (
    <Layout>
      <section className="w-full lg:w-3/4 mt-6">
        <div className="mb-4">
          {!isReadOnly && (
            <p className="text-sm font-medium">
              {t(
                'Choose accounting periods and corresponding distribution % or $ for total PO $ amount.'
              )}
            </p>
          )}
        </div>
        <div className="h-full">
          <GridWrapper
            id="budgetPeriodGrid"
            ref={gridRef}
            columns={columns}
            dataSource={budgetPeriodDataWithErrors}
            style={{ maxHeight: `${height - 164}px` }}
            detail={DetailComponent}
            cellRender={cellRender}
            onItemChange={handleItemChange}
            loading={
              isBudgetPeriodLoading || isPoDetailsLoading || isSummaryLoading || isDropDownLoading
            }
            {...defaultProps}
            headerCellRender={headerCellRender}
            gridToolbarChildren={!isReadOnly ? <ViewBudgetPeriodToolBar /> : undefined}
            isCustomExpandableRow
          />
        </div>
      </section>
    </Layout>
  );
};

export default ViewBudgetPeriodTable;
